import React, { useRef, useState, useEffect, useCallback } from 'react';
import MapImg from '@assets/images/MapImg.svg';
import DonutChart from '@components/UI/DonutChart';
import { useAppSelector } from '@store/hooks';
import { selectCurrentCategory, selectRegions } from '@store/mainReducer';
import { useViewportMode } from '@utils/useMobileDetection';

const getDonutSize = (total) => {
  if(total <= 0.5) {
    return 'SIZE_1';
  } else if (total <= 2) {
    return 'SIZE_2';
  } else if (total <= 4) {
    return 'SIZE_3';
  } else if (total >= 10) {
    return 'SIZE_4';
  }
  return 'SIZE_5';
}

const BASE_W = 674;
const BASE_H = 690;

const Map = () => {
  const regions = useAppSelector(selectRegions);
  const currentCategory = useAppSelector(selectCurrentCategory);
  const regionsOfCurrentCategory = regions.find(
    (region) => region.category === currentCategory
  );
  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0);
  const { isMobile } = useViewportMode();

  const updateScale = useCallback(() => {
    if (!containerRef.current) return;
    const parent = containerRef.current.parentElement;
    if (!parent) return;
    const availableW = parent.clientWidth;
    const scaleW = availableW > 0 ? availableW / BASE_W : 1;
    // Mobile: width-only scaling since vertical scroll is allowed
    if (isMobile) {
      setScale(Math.min(scaleW, 1));
    } else {
      const availableH = parent.clientHeight;
      if (availableH <= 0) {
        setScale(Math.min(scaleW, 1));
      } else {
        const scaleH = availableH / BASE_H;
        setScale(Math.min(scaleW, scaleH, 1));
      }
    }
  }, [isMobile]);

  useEffect(() => {
    // Use ResizeObserver for reliable measurement after layout
    const parent = containerRef.current?.parentElement;
    if (!parent) {
      updateScale();
      return;
    }

    const observer = new ResizeObserver(() => {
      updateScale();
    });
    observer.observe(parent);
    updateScale();

    window.addEventListener('resize', updateScale);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateScale);
    };
  }, [updateScale]);

  // Don't render until scale is computed to avoid flash
  if (scale === 0) {
    return <div ref={containerRef} style={{ width: '100%', minHeight: 1 }} />;
  }

  return (
    <div
      ref={containerRef}
      style={{ width: BASE_W * scale, height: BASE_H * scale, overflow: 'hidden' }}
    >
      <div
        className="relative text-center"
        style={{
          width: BASE_W,
          height: BASE_H,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
        }}
      >
        <img src={MapImg} alt="Map" />
        {regionsOfCurrentCategory.regions.map((region) => (
          <DonutChart
            key={region.label}
            className={region.label.toLowerCase()}
            data={region.items}
            size={getDonutSize(region.total)}
            total={region.total}
            currentCategory={currentCategory}
          />
        ))}
      </div>
    </div>
  );
};

export default Map;
